import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';
import back_n from "../Tutorials/Image/back_n.gif";
import back_o from "../Tutorials/Image/back_o.gif";
import back_d from "../Tutorials/Image/back_d.gif";
import principle_n from "../Tutorials/Image/principle_n.gif";
import tutorials_n from "../Tutorials/Image/tutorials_n.gif";
import clue_n from "../Tutorials/Image/clue_n.gif";
import solution_n from "../Tutorials/Image/solution_n.gif";
import solution_o from "../Tutorials/Image/solution_o.gif";
import solution_d from "../Tutorials/Image/solution_d.gif";
import next_n from "../Tutorials/Image/next_n.gif";
import next_o from "../Tutorials/Image/next_o.gif";
import next_d from "../Tutorials/Image/next_d.gif";
import ok_n from "../Tutorials/Image/ok_n.gif";
import ok_o from "../Tutorials/Image/ok_o.gif";
import ok_d from "../Tutorials/Image/ok_d.gif";
import done_o from "../Tutorials/Image/done_o.gif";
import done_n from "../Tutorials/Image/done_n.gif";
import x_close_btn_n from "../Tutorials/Image/x_close_btn_n.png";
import x_close_btn_o from "../Tutorials/Image/x_close_btn_o.png";
import next_step_btn_n from "../Tutorials/Image/next_step_btn_n.png";
import next_step_btn_o from "../Tutorials/Image/next_step_btn_o.png";
import got_it_btn_n from "../Tutorials/Image/got_it_btn_n.png";
import got_it_btn_o from "../Tutorials/Image/got_it_btn_o.png";
import './Style/Question.css';
import Loading from "../../Common/Loading";
import { buildNextQuestionData, buildOptionOrder ,buildShuffledQuestionsOrder, saveQuestionPerformance, QuestionPerformancePayload } from '../../services/questionApi';

    export interface AnswerRef {
        getAnswer: () => {
            marks: number;
            noOfOptions: number;
            enteredCorrectAnswer: string;
            enteredWrongAnswer: string;
            enteredBlankAnswer: string;
            optOrder?: string;
            scoringMethod?: number | null;
        } | null;
    }

    interface IQnsProps {
        lessonMeta?: any;
        questionHtml: string;
        currentPage: number;
        totalPages: number;
        goToNextPage?: (e: React.MouseEvent) => void;
        showAlert: (message: string) => void;
        correctAudio: string[];
        incorrectAudio: string[];
        assetsBaseUrl?: string;
        sessionId: number | null;
        isCompleting?: boolean;
        updateSessionPoints?: (pointsEarned: number) => void;
        hwid?: number | null;
        subject?: string;
        // NEW: admin "view lesson" preview support (mirrors ImageQns.tsx).
        // 'ANSWERS'  -> auto-reveal the correct answer(s) on load (from QNS-ANS button)
        // 'SOLUTION' -> auto-reveal the correct answer(s) AND auto-open the solution modal (from SOLUTION button)
        // undefined/null -> normal student flow, completely unaffected
        previewMode?: string | null;
        // NEW: true whenever this Question page was reached from the sysadmin
        // EL_ViewLesson page (TUT / QNS / QNS-ANS / SOLUTION / RFC buttons).
        // Used only to decide where "Done" navigates to. Real students never
        // get this flag, so their flow is unaffected.
        isViewLesson?: boolean;
    }

    const injectIdentificationBridge = (html: string): string => {
        let cleaned = html
            .replace(/onmousemove="[^"]*"/gi, "")
            .replace(/<script[\s\S]*?<\/script>/gi, "")
            .replace(/(<td[^>]*id="KSD_TD_[^"]*"[^>]*)\s+style="[^"]*"/gi, "$1");
            
        const bridge = `
            <style>
                body { margin: 0; padding: 0; }
                p { margin: 0; padding: 0; }
                
                #KSD_TDTYPEI {
                    text-align: center;
                }
                .clickable-item {
                    display: inline-flex; align-items: center; justify-content: center;
                    font-size: 32pt; padding: 5px 10px;
                    border: 2px solid transparent; cursor: pointer; user-select: none;
                    min-width: 40px; text-align: center;
                }
                .clickable-item:hover { border-color: red; }
                .clickable-item.selected { border-color: green; }
                .feedback-icon { margin-left: 5px; font-size: 20px; }
                .feedback-icon img { width: 17px; height: 17px; }
                .clickable-item.correct { border-color: green; }
                .clickable-item.incorrect { border-color: red; }
                .clickable-item.missed { border-color: grey; }
            </style>
            <script>
                document.addEventListener('DOMContentLoaded', function() {
                    try {
                        const items = document.querySelectorAll('.clickable-item');
                        items.forEach(item => {
                            item.addEventListener('click', function() {
                                this.classList.toggle('selected');
                                const selectedValues = Array.from(document.querySelectorAll('.clickable-item.selected'))
                                    .map(el => el.dataset.value);
                                parent.postMessage({ type: 'el-items-selected', selected: selectedValues }, '*');
                            });
                        });
                    } catch(e) { console.error("Bridge Error: ", e); }
                });
            </script>
        `;
        return cleaned.includes('</body>') ? cleaned.replace('</body>', bridge + '</body>') : cleaned + bridge;
    };

const IQns: React.ForwardRefRenderFunction<AnswerRef, IQnsProps> = (props, ref) => {
    const { lessonMeta, questionHtml, currentPage, totalPages, goToNextPage, showAlert, correctAudio, incorrectAudio, assetsBaseUrl, sessionId, isCompleting, updateSessionPoints, hwid, subject, previewMode, isViewLesson } = props;
    const url = process.env.REACT_APP_DOCUMENT_LINK ;
    const navigate = useNavigate();
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const solnIframeRef = useRef<HTMLIFrameElement>(null);
    const clueIframeRef = useRef<HTMLIFrameElement>(null);
    const prcIframeRef = useRef<HTMLIFrameElement>(null);

    // Is this an admin "view lesson" preview render (as opposed to a real student attempt)?
    const isPreview = previewMode === 'ANSWERS' || previewMode === 'SOLUTION';

    // Guards against re-running the auto-reveal more than once per question in preview mode
    const revealedForPageRef = useRef<number | null>(null);

    const [backSrc, setBackSrc] = useState(back_n);
    const [nextSrc, setNextSrc] = useState(next_n);
    const [okSrc, setOkSrc] = useState(ok_n);
    const [doneSrc, setDoneSrc] = useState(done_n);
    const [solutionSrc, setSolutionSrc] = useState(solution_n);
    const [CloseBtnSrc, setCloseBtnSrc] = useState(x_close_btn_n);
    const [NextStepBtnSrc, setNextStepBtnSrc] = useState(next_step_btn_n);
    const [GotItBtnSrc, setGotItBtnSrc] = useState(got_it_btn_n);
    
    const [okClicked, setOkClicked] = useState(false);
    const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
    const [currentQnMeta, setCurrentQnMeta] = useState<any | null>(null);

    const [showClueModal, setShowClueModal] = useState(false);
    const [clueContent, 
        setClueContent
    ] = useState('');
    const [isClueLoading, 
        setIsClueLoading
    ] = useState(false);
    const [noOfClues, setNoOfClues] = useState(0);
    const [currentClueNo, setCurrentClueNo] = useState(0);
    const [clueSeen, setClueSeen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [showPrcModal, setShowPrcModal] = useState(false);
    const [prcContent, setPrcContent] = useState("");
    const [isPrcLoading, 
        setIsPrcLoading
    ] = useState(false);
    const [prcSeen, setPrcSeen] = useState(false);
    const [principleList, setPrincipleList] = useState<string[]>([]);
    const [currentPrcIndex, setCurrentPrcIndex] = useState(0);

    const [showSolnModal, setShowSolnModal] = useState(false);
    const [isSolnLoading, 
        setIsSolnLoading
    ] = useState(false);
    const [solnContent, 
        setSolnContent
    ] = useState("");
    const [currentSolnNo, setCurrentSolnNo] = useState(0);
  const [revealedSolnSteps, setRevealedSolnSteps] = useState<number[]>([]);
    useEffect(() => {
      setCurrentSolnNo(0);
      setRevealedSolnSteps([]);
    }, [currentQnMeta?.QnNo]);
    const [noOfSteps, setNoOfSteps] = useState(0);
    const [solutionSeen, setSolutionSeen] = useState<string | number>('N');

    const search = window.location.search;
    const params = new URLSearchParams(search);
    const folderParam = params.get('folder');
    const lessonName = params.get('name');
    const diccode = params.get('diccode');
    const calledFrom = params.get('calledFrom');

    const handleNextStepBtnMouseOver = () => setNextStepBtnSrc(next_step_btn_o);
    const handleNextStepBtnMouseOut = () => setNextStepBtnSrc(next_step_btn_n);
    const handleGotItBtnMouseOver = () => setGotItBtnSrc(got_it_btn_o);
    const handleGotItBtnMouseOut = () => setGotItBtnSrc(got_it_btn_n);
    const handleCloseBtnMouseOver = () => setCloseBtnSrc(x_close_btn_o);
    const handleCloseBtnMouseOut = () => setCloseBtnSrc(x_close_btn_n);
    const handleBackMouseOver = () => setBackSrc(back_o);
    const handleBackMouseOut = () => setBackSrc(back_n);
    const handleBackMouseDown = () => setBackSrc(back_d);
    const handleBackMouseUp = () => setBackSrc(back_o);
    const handleNextMouseOver = () => setNextSrc(next_o);
    const handleNextMouseOut = () => setNextSrc(next_n);
    const handleNextMouseDown = () => setNextSrc(next_d);
    const handleNextMouseUp = () => setNextSrc(next_o);
    const handleOkMouseOver = () => setOkSrc(ok_o);
    const handleOkMouseOut = () => setOkSrc(ok_n);
    const handleOkMouseDown = () => setOkSrc(ok_d);
    const handleOkMouseUp = () => setOkSrc(ok_o);
    const handleSolutionMouseOver = () => setSolutionSrc(solution_o);
    const handleSolutionMouseOut = () => setSolutionSrc(solution_n);
    const handleSolutionMouseDown = () => setSolutionSrc(solution_d);
    const [startTime, setStartTime] = useState<Date | null>(null);
     const isInAssessment = window.location.href.includes('Assessment');

    const correctAnswer = `https://staging.elearnoncloud.com/Storage/assets/Image/Questions/EL_I/AnsCorrect.png`;
    const wrongAnswer = `https://staging.elearnoncloud.com/Storage/assets/Image/Questions/EL_I/AnsWrong.png`;

const handleClueClick = useCallback(async () => {
    if (!currentQnMeta || !lessonMeta) return;

    setShowClueModal(true);
    setIsClueLoading(true);
    setClueContent("");
    setCurrentClueNo(0);
    setNoOfClues(0);

    const searchParams = new URLSearchParams(window.location.search);
    const folderParam = searchParams.get('folder');
    const countryParam = params.get('country') || 'Australia';
    const varValuesParam = currentQnMeta?.VarValues || "";

    if (!folderParam) {
        showAlert("Could not determine the lesson folder.");
        setIsClueLoading(false);
        setShowClueModal(false);
        return;
    }

    const accessToken = localStorage.getItem('access_token');
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
    } else {
        console.warn("Access token not found in localStorage.");
    }

    const apiUrl = process.env.REACT_APP_DOCUMENT_LINK ;

    try {
        const response = await fetch(`${apiUrl}clue/`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({
                folder: folderParam,
                questionNo: currentQnMeta.QnNo,
                country: countryParam,
                varValues: varValuesParam,
            }),
        });

        if (!response.ok) {
            if (response.status === 404) {
                setClueContent("");
            } else {
                if (response.status === 401 || response.status === 403) {
                    showAlert("Authentication failed. Please log in again.");
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }
        } else {
            const data = await response.json();
            setClueContent(data.clueHtml || "");
            setClueSeen(true);
        }

    } catch (error) {
        console.error("Error fetching clue:", error);
        if (!(error instanceof Error && (error.message.includes("status: 401") || error.message.includes("status: 403") || error.message.includes("status: 404")))) {
            showAlert("Failed to load the clue. Please try again.");
        }
        setClueContent("");
    } finally {
        setIsClueLoading(false);
    }
}, [currentQnMeta, lessonMeta, showAlert, params]);

const fetchPrincipleContent = async (principleId: string, folderParam: string, countryParam: string) => {
        const accessToken = localStorage.getItem('access_token');
        const headers: HeadersInit = { 'Content-Type': 'application/json' };
        if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
        
        const apiUrl = process.env.REACT_APP_DOCUMENT_LINK ;

        const response = await fetch(`${apiUrl}principle/`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({
                folder: folderParam,
                questionNo: principleId,
                country: countryParam,
            }),
        });

        if (!response.ok) throw new Error("Failed");
        return await response.json();
    };

const handlePrcClick = useCallback(async () => {
        if (!currentQnMeta || !lessonMeta) return;

        const prcString = currentQnMeta.PrincipleNos || ""; 
        const prcIds = prcString.replace(/"/g, "").split(',').filter((s: string) => s.trim() !== "");
        
        if (prcIds.length === 0) {
            if((currentQnMeta.NoOfPrc || 0) > 0) {
                prcIds.push("1");
            } else {
                showAlert("No principles found for this question.");
                return;
            }
        }

        setShowPrcModal(true);
        setIsPrcLoading(true);
        setPrcContent("");
        setPrincipleList(prcIds);
        setCurrentPrcIndex(0);

        const searchParams = new URLSearchParams(window.location.search);
        const folderParam = searchParams.get('folder');
        const countryParam = params.get('country') || 'Australia';

        if (!folderParam) {
            showAlert("Could not determine the lesson folder.");
            setIsPrcLoading(false);
            setShowPrcModal(false);
            return;
        }

        try {
            const data = await fetchPrincipleContent(prcIds[0], folderParam, countryParam);
            setPrcContent(data.principleHtml || "");
            setPrcSeen(true);
        } catch (error) {
            console.error("Error fetching principle:", error);
            setPrcContent("");
        } finally {
            setIsPrcLoading(false);
        }
    }, [currentQnMeta, lessonMeta, showAlert, params]);

const handleSolnClick = useCallback(async (autoExpand = false) => {
        if (!currentQnMeta || !lessonMeta) return;

        setShowSolnModal(true);
        setIsSolnLoading(true);
        setSolnContent("");
        setNoOfSteps(0);

        const searchParams = new URLSearchParams(window.location.search);
        const folderParam = searchParams.get('folder');
        const countryParam = searchParams.get('country') || 'Australia';

        if (!folderParam) {
            showAlert("Could not determine the lesson folder.");
            setIsSolnLoading(false);
            setShowSolnModal(false);
            return;
        }

        const accessToken = localStorage.getItem('access_token');
        const headers: HeadersInit = { 'Content-Type': 'application/json' };
        if (accessToken) {
            headers['Authorization'] = `Bearer ${accessToken}`;
        } else {
            console.warn("Access token not found in localStorage.");
        }

        const apiUrl = process.env.REACT_APP_DOCUMENT_LINK ;

        try {
            const response = await fetch(`${apiUrl}solution/`, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify({
                    folder: folderParam,
                    questionNo: currentQnMeta.QnNo,
                    country: countryParam,
                    autoExpand: autoExpand,
                }),
            });

            if (!response.ok) {
                if (response.status === 401 || response.status === 403) {
                    showAlert("Authentication failed. Please log in again.");
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setSolnContent(data.solutionHtml || "");
            if (solutionSeen === 'N') {
                setSolutionSeen(1);
            }

        } catch (error) {
            console.error("Error fetching solution:", error);
            if (!(error instanceof Error && (error.message.includes("status: 401") || error.message.includes("status: 403")))) {
                showAlert("Failed to load the solution. Please try again.");
            }
            setShowSolnModal(false);
        } finally {
            setIsSolnLoading(false);
        }
    }, [currentQnMeta, lessonMeta, showAlert, solutionSeen]);


    useEffect(() => {
        setSelectedItems(new Set());
        setOkClicked(false);
        setSolutionSeen('N');
        setPrcSeen(false);
        revealedForPageRef.current = null; // NEW: reset preview-reveal guard per question
        if (lessonMeta?.qns) setCurrentQnMeta(lessonMeta.qns[currentPage - 1]);
        setStartTime(new Date());
    }, [currentPage, lessonMeta, questionHtml]);

    useEffect(() => {
        const iframe = iframeRef.current;
        if (iframe && questionHtml) iframe.srcdoc = injectIdentificationBridge(questionHtml);
    }, [questionHtml]);

    useEffect(() => {
        const onMessage = (e: MessageEvent) => {
            if (e.data?.type === 'el-items-selected') {
                setSelectedItems(new Set(e.data.selected));
            }
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, []);
    
    useImperativeHandle(ref, () => ({
        getAnswer: () => {
            if (selectedItems.size === 0) {
                showAlert("Please answer the question.");
                return null;
            }

            const iframe = iframeRef.current;
            if (!iframe?.contentDocument) return null;
            const iframeDoc = iframe.contentDocument;

            const answerKeyEl = iframeDoc.getElementById('answer-key') as HTMLInputElement;
            if (!answerKeyEl) {
                showAlert("Could not find answer key in the question.");
                return null;
            }
            const answerKey = JSON.parse(answerKeyEl.value);
            const noOfOptions = Object.keys(answerKey).length;

            const enteredCorrect: string[] = [];
            const enteredWrong: string[] = [];
            const enteredBlank: string[] = [];

            Object.entries(answerKey).forEach(([number, isCorrect]) => {
                const wasSelected = selectedItems.has(number);
                if (isCorrect === 'Y') {
                    if (wasSelected) enteredCorrect.push(number);
                    else enteredBlank.push(number);
                } else {
                    if (wasSelected) enteredWrong.push(number);
                }
            });

            const isPerfectScore = enteredWrong.length === 0 && enteredBlank.length === 0;
            const marks = isPerfectScore ? 1 : 0;

            return {
                marks,
                noOfOptions,
                enteredCorrectAnswer: enteredCorrect.join('|'),
                enteredWrongAnswer: enteredWrong.join('|'),
                enteredBlankAnswer: enteredBlank.join('|'),
                optOrder: "",
                scoringMethod: null
            };
        }
    }));

const playRandomSound = useCallback(async (soundFiles: string[], folder: 'Correct' | 'Incorrect') => {
    const baseUrl = assetsBaseUrl;

    if (!soundFiles || soundFiles.length === 0) {
        console.error("Cannot play sound: No sound files were provided.");
        return;
    }
    const fileName = soundFiles[Math.floor(Math.random() * soundFiles.length)];
    const audioUrl = `${baseUrl}Audio/Question/${folder}/${fileName}`;

    const audio = new Audio(audioUrl);

    try {
        await audio.play();
    } catch (error) {
        console.error(`Error playing audio from ${audioUrl}:`, error);
    }
}, [assetsBaseUrl]);

const handleDbUpdate = useCallback((payload: QuestionPerformancePayload) => {
    // NEW: Never persist performance/points data for admin preview renders
    // (QNS-ANS or SOLUTION buttons from sysadmin ViewLesson). Keeps real
    // student progress/session data completely untouched.
    if (isPreview) {
        return Promise.resolve(null as any);
    }
    return saveQuestionPerformance(payload)
        .then((data) => {
            if (isCompleting && updateSessionPoints) {
                updateSessionPoints(data.pointsEarned || 0);
            }
            return data;
        })
        .catch((error) => {
            console.error("Save Performance Error:", error);
            throw error;
        });
}, [isCompleting, updateSessionPoints, isPreview]);

// ── ADMIN PREVIEW ONLY ────────────────────────────────────────────────
// Marks every answer-key item flagged 'Y' as selected/correct, shows the
// same feedback icon + explanation the real OK-click flow shows, and
// freezes the items — mirroring handleOkClick's feedback rendering but
// without requiring any user click and without touching DB writes.
// Only ever runs when previewMode is set (i.e. only when reached via the
// sysadmin ViewLesson "QNS-ANS" or "SOLUTION" buttons). Real student flow
// never sets previewMode, so this code path never executes for students.
const autoRevealCorrectAnswer = useCallback(() => {
    const iframeDoc = iframeRef.current?.contentDocument;
    if (!iframeDoc) return;

    const answerKeyEl = iframeDoc.getElementById('answer-key') as HTMLInputElement;
    if (!answerKeyEl) return;

    let answerKey: Record<string, string> = {};
    try {
        answerKey = JSON.parse(answerKeyEl.value);
    } catch (e) {
        console.error("Preview: failed to parse answer key", e);
        return;
    }

    const correctValues: string[] = [];

    Object.entries(answerKey).forEach(([number, isCorrect]) => {
        const itemEl = Array.from(iframeDoc.querySelectorAll<HTMLElement>('.clickable-item'))
            .find(item => item.dataset.value === number);
        if (!itemEl) return;

        (itemEl as HTMLElement).style.pointerEvents = 'none';

        if (isCorrect === 'Y') {
            correctValues.push(number);
            itemEl.classList.add('selected', 'correct');
            itemEl.innerHTML += `<span class="feedback-icon"><img src="${correctAnswer}" alt="Correct"/></span>`;
        }
    });

    setSelectedItems(new Set(correctValues));

    const infoInput = iframeDoc.getElementById('KSD_MW_Info') as HTMLInputElement;
    let explanationHtml = "";

    if (infoInput && infoInput.value) {
        const parts = infoInput.value.split('|');
        if (parts.length > 0) {
            explanationHtml = parts[parts.length - 1];
        }
    }

    if (explanationHtml && explanationHtml !== "0N<NEW LINE>" && !iframeDoc.getElementById("el-explanation-container")) {
        const container = iframeDoc.createElement("div");
        container.id = "el-explanation-container";
        container.innerHTML = `
            <hr style='height:1px; background-color:#cfcfcf; border:none; margin-top:50px; margin-bottom:30px; margin-left:30px; margin-right:30px;'>
            <p style='font-size:24px; color:#797979; font-family:Arial,Helvetica,sans-serif; line-height:30px; padding-left:30px; padding-right:30px; margin-top:0px; margin-bottom:20px'>Explanation</p>
            <p style='font-size:24px; color:#000000; font-family:Arial,Helvetica,sans-serif; line-height:30px; padding-left:30px; padding-right:30px; margin-top:0px; margin-bottom:50px'>
                ${explanationHtml}
            </p>
        `;
        iframeDoc.body.appendChild(container);
        setTimeout(() => {
            iframeDoc.documentElement.scrollTop = iframeDoc.documentElement.scrollHeight;
        }, 100);
    }

    setOkClicked(true);

    if (previewMode === 'SOLUTION' && currentQnMeta?.NoOfSol && currentQnMeta.NoOfSol > 0) {
        handleSolnClick(true);
    }
}, [previewMode, currentQnMeta, handleSolnClick, correctAnswer]);

// Fires once per question, only in preview mode, once the iframe content is ready.
const handleQuestionIframeLoad = useCallback(() => {
    if (!isPreview) return;
    if (revealedForPageRef.current === currentPage) return; // already revealed for this question
    revealedForPageRef.current = currentPage;
    autoRevealCorrectAnswer();
}, [isPreview, currentPage, autoRevealCorrectAnswer]);
// ── END ADMIN PREVIEW ONLY ───────────────────────────────────────────

const handleOkClick = useCallback(async (e: React.MouseEvent<any>) => {
    e.preventDefault();
    if (okClicked || isSubmitting) return;

    if (selectedItems.size === 0) {
        showAlert("Please answer the question.");
        return;
    }

    const iframe = iframeRef.current;
    if (!iframe?.contentDocument) return;
    const iframeDoc = iframe.contentDocument;

    const answerKeyEl = iframeDoc.getElementById('answer-key') as HTMLInputElement;
    if (!answerKeyEl) {
        showAlert("Could not find answer key in the question.");
        return;
    }
    
    const answerKey = JSON.parse(answerKeyEl.value);
    const noOfOptions = Object.keys(answerKey).length;

    const enteredCorrect: string[] = [];
    const enteredWrong: string[] = [];
    const enteredBlank: string[] = [];
    let totalCorrectOptions = 0;
    let correctlySelectedCount = 0;

    Object.entries(answerKey).forEach(([number, isCorrect]) => {
        const wasSelected = selectedItems.has(number);
        if (isCorrect === 'Y') {
            totalCorrectOptions++;
            if (wasSelected) {
                correctlySelectedCount++;
                enteredCorrect.push(number);
            } else {
                enteredBlank.push(number);
            }
        } else {
            if (wasSelected) {
                enteredWrong.push(number);
            }
        }
    });

    const isPerfectScore = enteredWrong.length === 0 && enteredBlank.length === 0;
    const marks = isPerfectScore ? 1 : 0;

    const questionAnswerTime = startTime ? Math.floor((new Date().getTime() - startTime.getTime()) / 1000) : 0;
    
    const schoolCode = sessionStorage.getItem('SchoolCode') || ''; 
    const familyCode = sessionStorage.getItem('FamilyCode') || '';
    const login = sessionStorage.getItem('Login') || '';
    const chapterCode = sessionStorage.getItem('ChapterCode') || '';
    const unitCode = sessionStorage.getItem('UnitCode') || '';

    // --- PAYLOAD PREP ---
    const payload: QuestionPerformancePayload = {
        schoolCode,
        familyCode,
        login,
        dicCode: sessionStorage.getItem('SelectedDICFile') || '',
        lsnTestCode: sessionStorage.getItem('LsnTestCode') || '',
        sessionId: sessionId || 1,
        qnNo: currentQnMeta?.QnNo,
        subNo: 0,
        qnType: currentQnMeta?.QnType,
        marks: marks,
        noOfOptions: noOfOptions,
        enteredCorrectAnswer: enteredCorrect.join('|'),
        enteredWrongAnswer: enteredWrong.join('|'),
        enteredBlankAnswer: enteredBlank.join('|'),
        solutionSeen: (typeof solutionSeen === 'number' && solutionSeen > 0) || solutionSeen === 'Y' ? 'Y' : 'N',
        clueSeen: clueSeen,
        principleSeen: prcSeen,
        questionLoadTime: 0,
        questionAnswerTime: questionAnswerTime,
        lastQnInd: currentQnMeta?.LastQnInd || currentPage,
        nextQnData: buildNextQuestionData(lessonMeta?.qns, currentPage),
        dbgShuffledQnsOrder: buildShuffledQuestionsOrder(lessonMeta?.qns),
        allQuestionsCompleted: isCompleting ? 'True' : 'False',
        chapterCode: chapterCode,
        unitCode: unitCode,
        hwid: hwid,
        subject: subject
    };

    // --- HYBRID SUBMISSION LOGIC ---
    const isLastQuestion = currentPage === totalPages;

    if (isLastQuestion) {
        // BLOCKING: Show Loading, Wait for DB
        setIsSubmitting(true);
        try {
            await handleDbUpdate(payload);
        } catch(e) {
            console.error("Final submit failed", e);
            showAlert("Error saving progress. Please try again.");
            setIsSubmitting(false);
            return;
        }
        setIsSubmitting(false);
    } else {
        // BACKGROUND: Fire and forget
        handleDbUpdate(payload).catch(e => console.error("Background save failed", e));
    }
    
    // --- UI FEEDBACK ---
    const allItems = iframeDoc.querySelectorAll('.clickable-item');
    allItems.forEach(item => {
        (item as HTMLElement).style.pointerEvents = 'none';
        item.classList.remove('selected');
    });

    Object.entries(answerKey).forEach(([number, isCorrect]) => {
        const itemEl = Array.from(iframeDoc.querySelectorAll<HTMLElement>('.clickable-item'))
            .find(item => item.dataset.value === number);
        if (!itemEl) return;
        const wasSelected = selectedItems.has(number);

        if (isCorrect === 'Y') {
            if (wasSelected) {
                itemEl.classList.add('correct');
                itemEl.innerHTML += `<span class="feedback-icon"><img src="${correctAnswer}" alt="Correct"/></span>`;
            } else {
                itemEl.classList.add('missed');
            }
        } else {
            if (wasSelected) {
                itemEl.classList.add('incorrect');
                itemEl.innerHTML += `<span class="feedback-icon"><img src="${wrongAnswer}" alt="Incorrect"/></span>`;
            }
        }
    });

    const infoInput = iframeDoc.getElementById('KSD_MW_Info') as HTMLInputElement;
    let explanationHtml = "";
    
    if (infoInput && infoInput.value) {
        const parts = infoInput.value.split('|');
        if (parts.length > 0) {
            explanationHtml = parts[parts.length - 1];
        }
    }
    
    if (explanationHtml && explanationHtml !== "0N<NEW LINE>" && !iframeDoc.getElementById("el-explanation-container")) {
        const container = iframeDoc.createElement("div");
        container.id = "el-explanation-container";
        container.innerHTML = `
            <hr style='height:1px; background-color:#cfcfcf; border:none; margin-top:50px; margin-bottom:30px; margin-left:30px; margin-right:30px;'>
            <p style='font-size:24px; color:#797979; font-family:Arial,Helvetica,sans-serif; line-height:30px; padding-left:30px; padding-right:30px; margin-top:0px; margin-bottom:20px'>Explanation</p>
            <p style='font-size:24px; color:#000000; font-family:Arial,Helvetica,sans-serif; line-height:30px; padding-left:30px; padding-right:30px; margin-top:0px; margin-bottom:50px'>
                ${explanationHtml}
            </p>
        `;
        iframeDoc.body.appendChild(container);
        setTimeout(() => {
            iframeDoc.documentElement.scrollTop = iframeDoc.documentElement.scrollHeight;
        }, 100);
    }

    if (isPerfectScore) {
        playRandomSound(correctAudio, 'Correct');
    } else {
        playRandomSound(incorrectAudio, 'Incorrect');

        if (currentQnMeta?.NoOfSol && currentQnMeta.NoOfSol > 0) {
            handleSolnClick(true);
        }
    }

    setOkClicked(true);
}, [
    selectedItems, okClicked, showAlert, correctAnswer, wrongAnswer, 
    correctAudio, incorrectAudio, playRandomSound, startTime, lessonMeta, 
    currentQnMeta, currentPage, totalPages, sessionId, isCompleting, 
    handleDbUpdate,
    handleSolnClick, solutionSeen, clueSeen, prcSeen, hwid, subject
]);

    const handleBack = () => {
        const calledFrom = params.get("calledFrom"); 
        
        // console.log("calledFrom:", calledFrom);
        if (calledFrom) {
        const redirectPath = decodeURIComponent(calledFrom);
        window.location.href = redirectPath;
        } 
        else if (window.history.length > 1) {
        window.history.back();
        }
    };

    // ── ADMIN PREVIEW ONLY ────────────────────────────────────────────────
    // When this page was opened from sysadmin's EL_ViewLesson (TUT / QNS /
    // QNS-ANS / SOLUTION / RFC buttons), "Done" must go straight back to
    // that ViewLesson page — not through /STUDENT/Tutorials/Result, which
    // expects a real student session. For real students (isViewLesson
    // falsy) this is unreachable — the Done onClick only ever ran the
    // navigate(...) branch, and still does when isViewLesson is false.
    const handleDoneClick = () => {
        if (isViewLesson) {
            if (calledFrom) {
                window.location.href = decodeURIComponent(calledFrom);
            } else if (window.history.length > 1) {
                window.history.back();
            }
            return;
        }

        const dicCode = sessionStorage.getItem('SelectedDICFile') || '';
        const lsnTestCode = sessionStorage.getItem('LsnTestCode') || '';
        const calledFromNav = params.get("calledFrom") || "";
        navigate(
            `/STUDENT/Tutorials/Result?DicCode=${dicCode}&LsnTestCode=${lsnTestCode}&SessionID=${sessionId}&source=lesson&complete=1` +
            (calledFromNav ? `&calledFrom=${encodeURIComponent(calledFromNav)}` : "")
        );
    };
    // ── END ADMIN PREVIEW ONLY ───────────────────────────────────────────
    
    return (
        <>
                {!isInAssessment && (
                                <tr id="trQnButtons">
                <td className="QnPanelT">
                    <table id="tabQnNav" className="QnPanel2T" style={{ height: "50px" }}>
                        <tbody>
                            <tr id="trQnOK" className="trQnOKT">
                                <td id="QnPanelIdT" className="QnPanel">&nbsp;</td>
                                <td id="QnPanelId2T" className="QnPanel">
                          <div 
                            onClick={!okClicked ? handleBack : undefined}
                            style={{ display: okClicked ? 'none' : 'block', cursor: 'pointer' }}
                          >
                            <div id="imgbtnBack" className="imgbtnBackT">
                              <img
                                src={backSrc}
                                alt="Back"
                                onMouseOver={handleBackMouseOver}
                                onMouseOut={handleBackMouseOut}
                                onMouseDown={handleBackMouseDown}
                                onMouseUp={handleBackMouseUp}
                                className="imgbtnBack2T"
                              />
                            </div>
                          </div>
                        </td>
                      <td id="QnPanelId3T" className="QnPanel">
                          <input
                            type="image"
                            id="imgPrc"
                            src={principle_n}
                            className="imgPrcT"
                            alt="Principle"
                            style={{ display: !okClicked && currentQnMeta?.NoOfPrc && currentQnMeta.NoOfPrc > 0 ? 'inline-block' : 'none' }}
                            onClick={(e) => { e.preventDefault(); handlePrcClick(); }}
                          />
                        </td>
                            <td id="QnPanelId4T" className="QnPanel">
                      <input
                        type="image"
                        id="imgClue"
                        src={clue_n}
                        className="imgClueT"
                        alt="Clue"
                        style={{ display: !okClicked && Number(currentQnMeta?.NoOfClues || 0) > 0 ? 'inline-block' : 'none' }}
                        onClick={(e) => { e.preventDefault(); handleClueClick(); }}
                      />
                    </td>
                                <td id="QnPanelId5T" className="QnPanel">
                                  <input
                                    type="image" id="imgSoln" src={solutionSrc} className="imgSolnT" alt="Solution"
                                    style={{ display: currentQnMeta?.NoOfSol && currentQnMeta.NoOfSol > 0 ? 'inline-block' : 'none' }}
                                    onClick={(e) => { e.preventDefault(); handleSolnClick(false); }}
                                    onMouseOver={handleSolutionMouseOver}
                                    onMouseOut={handleSolutionMouseOut}
                                    onMouseDown={handleSolutionMouseDown}
                                  />
                                </td>
                                <td id="QnPanelId5T" className="QnPanel">
                                  <a 
                                    href={`/STUDENT/Tutorials/Tutorial?id=${folderParam}&name=${encodeURIComponent(lessonName ?? '')}&folder=${encodeURIComponent(diccode ?? '')}&calledFrom=${encodeURIComponent(calledFrom ?? '')}&sessionId=${sessionId}&currentPage=${currentPage}`} 
                                    id="imgbtnTut" 
                                    className="imgbtnTutT" 
                                    style={{ display: okClicked ? 'none' : 'inline-block' }}
                                  >
                                    <img src={tutorials_n} alt="Tutorial" id="imgbtnTut2T" />
                                  </a>
                                </td>
                                <td id="QnPanelId6T" className="QnPanel">
                                    {isSubmitting ? (
                                        <Loading />
                                    ) : (
                                        okClicked ? (
                                            currentPage === totalPages ? (
                                                <img 
                                                    src={doneSrc} 
                                                    alt="Done" 
                                                    style={{ verticalAlign: 'middle', cursor: 'pointer' }} 
                                                    onClick={handleDoneClick} 
                                                    onMouseOver={() => setDoneSrc(done_o)}
                                                    onMouseOut={() => setDoneSrc(done_n)}
                                                />
                                            ) : (
                                                <img src={nextSrc} alt="Next" onClick={goToNextPage} onMouseOver={handleNextMouseOver} onMouseOut={handleNextMouseOut}
                                                onMouseDown={handleNextMouseDown} onMouseUp={handleNextMouseUp} style={{ display: 'inline-block', cursor: 'pointer', marginTop: '3px' }}/>
                                            )
                                        ) : (
                                            <div onClick={handleOkClick} onMouseOver={handleOkMouseOver} onMouseOut={handleOkMouseOut} 
                                            onMouseDown={handleOkMouseDown} onMouseUp={handleOkMouseUp} style={{ display: 'inline-block', cursor: 'pointer' }}>
                                                <img src={okSrc} alt="OK" style={{ marginTop: '3px' }} />
                                            </div>
                                        )
                                    )}
                                </td>
                                <td id="QnPanelId7" className="QnPanel">&nbsp;</td>
                            </tr>
                            <tr id="trQnNext" className="trQnNextT">
                                <td id="tdBN1" className="QnPanel">&nbsp;</td>
                                <td id="tdAnsMsg" className="QnPanel">&nbsp;</td>
                                <td id="tdShowCA" className="QnPanel">&nbsp;</td>
                                <td id="tdAnsSoln" className="QnPanel">
                                    <img id="imgSolnAfterAnswering" src={solutionSrc} alt="Solution" onMouseOver={handleSolutionMouseOver} 
                                    onMouseOut={handleSolutionMouseOut} onMouseDown={handleSolutionMouseDown}/>
                                </td>
                                <td id="tdQnNext" className="QnPanel">
                                    {okClicked ? (
                                        <input type="image" id="imgNextQuestion" src={nextSrc} alt="Next" onClick={goToNextPage} 
                                        onMouseOver={handleNextMouseOver} onMouseOut={handleNextMouseOut} onMouseDown={handleNextMouseDown} 
                                        onMouseUp={handleNextMouseUp} style={{ display: okClicked ? 'inline-block' : 'none' }} />
                                    ) : (
                                        <img id="imgOK" src={okSrc} alt="OK" onClick={handleOkClick} onMouseOver={handleOkMouseOver} 
                                        onMouseOut={handleOkMouseOut} onMouseDown={handleOkMouseDown} onMouseUp={handleOkMouseUp} className="imgOKT" style={{ display: okClicked ? 'none' : 'inline-block' }}/>
                                    )}
                                </td>
                                <td id="tdBN" className="QnPanel">&nbsp;</td>
                            </tr>
                        </tbody>
                    </table>
                </td>
            </tr>
                )}

        <tr id="triFrame">
        <td id="tdFrameQnT" style={{ padding: 0, height: '100%' }}>
          <div id="divFrameQn" style={{ height: 'calc(95vh - 50px)', width: '100%', display: 'flex', flexDirection: 'column' }}>
            {questionHtml ? (
              <iframe
                id="iFrameQn"
                ref={iframeRef}
                frameBorder={0}
                scrolling="yes" 
                className="CssiFrm"
                style={{ 
                    flex: '1 1 0', 
                    width: '100%', 
                    height: '100%', 
                    minHeight: 0, 
                    border: 'none', 
                    overflow: 'auto' 
                }}
                title="Question Page"
                onLoad={handleQuestionIframeLoad}
              />
            ) : <Loading />}
          </div>
        </td>
      </tr>

            {showClueModal && (
                <div className="modal-overlay" onClick={() => setShowClueModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <span>Clue</span>
                            <img src={CloseBtnSrc} alt="Close" onClick={() => setShowClueModal(false)} onMouseOver={handleCloseBtnMouseOver} 
                            onMouseOut={handleCloseBtnMouseOut} style={{ cursor: 'pointer' }} />
                        </div>
                        <div className="modal-body">
                            {isClueLoading ? <Loading /> : (
                                <iframe
                                id="iFrameClue"
                                ref={clueIframeRef}
                                title="Clue"
                                srcDoc={clueContent}
                                style={{ width: '100%', border: 'none', minHeight: '80px' }}
                                onLoad={() => {
                                  //   console.log("Clue iframe onLoad event fired.");
                                    const iframe = clueIframeRef.current;
                                    if (!iframe || !iframe.contentDocument || !iframe.contentWindow) { return; }
                                    try {
                                        const divShows = iframe.contentDocument.querySelectorAll("[id^='divShow']");
                                        divShows.forEach(div => {
                                            (div as HTMLElement).style.padding = "20px";
                                        });
                                        const cluesInput = iframe.contentDocument.getElementById('NoOfClues') as HTMLInputElement;
                                        const totalClues = parseInt(cluesInput?.value || '0', 10);
                                        setNoOfClues(totalClues);
                                        const clueDivs = iframe.contentDocument.querySelectorAll<HTMLElement>('[id^="divShow"]');
                                        clueDivs.forEach((div, index) => {
                                            div.style.display = index === 0 ? 'block' : 'none';
                                        });
                                        const firstClueDiv = iframe.contentDocument.getElementById('divShow1');
                                        if (firstClueDiv) {
                                            const newHeight = firstClueDiv.scrollHeight;
                                            iframe.style.height = `${Math.min(newHeight, 300)}px`;
                                        } else if (totalClues === 0) {
                                            iframe.style.height = '50px';
                                        }
                                    } catch (e) { console.error("Error inside clue iframe onLoad:", e); }
                                }}
                                />
                            )}
                        </div>
                        <div className="modal-footer">
                            {!isClueLoading && noOfClues > 1 && currentClueNo < noOfClues - 1 && (
                                <img
                                    id="btnClueNext"
                                    src={NextStepBtnSrc}
                                    alt="Next Clue"
                                    onClick={() => {
                                        const nextClue = currentClueNo + 1;
                                        const iframe = clueIframeRef.current;
                                        if (iframe?.contentDocument) {
                                            iframe.contentDocument.querySelectorAll<HTMLElement>('[id^="divShow"]').forEach((div, index) => {
                                                div.style.display = index === nextClue ? 'block' : 'none';
                                            });
                                            setCurrentClueNo(nextClue);
                                            const nextClueDiv = iframe.contentDocument.getElementById(`divShow${nextClue + 1}`);
                                            if (nextClueDiv) {
                                                const newHeight = nextClueDiv.scrollHeight + 20;
                                                iframe.style.height = `${Math.min(newHeight, 300)}px`;
                                            }
                                        }
                                    }}
                                    onMouseOver={handleNextStepBtnMouseOver}
                                    onMouseOut={handleNextStepBtnMouseOut}
                                    style={{ cursor: 'pointer' }}
                                />
                            )}
                            {!isClueLoading && (noOfClues <= 1 || currentClueNo === noOfClues - 1) && (
                                <img
                                    id="btnClueGotIt"
                                    src={GotItBtnSrc}
                                    alt="Got It"
                                    onClick={() => setShowClueModal(false)}
                                    onMouseOver={handleGotItBtnMouseOver}
                                    onMouseOut={handleGotItBtnMouseOut}
                                    style={{ cursor: 'pointer', display: isClueLoading ? 'none' : 'inline-block' }}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}

    {showPrcModal && (
        <>
          <div id="divReadOnlyScreen" style={{ display: 'block' }} onClick={() => setShowPrcModal(false)}></div>
          
          <div id="divPCS" style={{ display: 'block' }} className="principle">
            
            {/* Header */}
            <table id="tblTitlePCS" className="principle" cellSpacing="0" cellPadding="0" width="100%">
              <tbody>
                <tr>
                  <td id="tdPCSTitle1" style={{ paddingLeft: '15px', color: 'white', fontWeight: 'bold', fontSize: '24px' }}>
                    Principle {principleList.length > 1 ? ` ${currentPrcIndex + 1} of ${principleList.length}` : ''}
                  </td>
                  <td id="tdPCSClose" align="right" valign="top">
                    <img 
                      id="imgPCSClose" 
                      src={CloseBtnSrc} 
                      alt="Close" 
                      onClick={() => { setShowPrcModal(false); setIsPrcLoading(false); }} 
                      onMouseOver={handleCloseBtnMouseOver} 
                      onMouseOut={handleCloseBtnMouseOut} 
                      style={{ cursor: 'pointer', padding: '5px', width: '40px', height: '40px', marginRight: '30px' }} 
                    />
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Body */}
            <div id="divFramePCS">
              {isPrcLoading ? <Loading /> : (
                <iframe 
                    id="iFramePrc" 
                    ref={prcIframeRef}
                    title="Prc" 
                    srcDoc={prcContent} 
                    style={{ width: '100%', border: 'none', overflow: 'hidden', display: 'block' }} // 2. Set overflow hidden initially
                    onLoad={() => {
                        const iframe = prcIframeRef.current;
                        if (!iframe || !iframe.contentDocument || !iframe.contentWindow) return;

                        setTimeout(() => {
                          if (!iframe.contentDocument || !iframe.contentDocument.body) return;
                          
                          const contentHeight = iframe.contentDocument.body.scrollHeight + 20;
                          
                          const divPCS = document.getElementById('divPCS') as HTMLElement;
                          const titleTable = document.getElementById('tblTitlePCS') as HTMLElement;
                          const navTable = document.getElementById('tblNavPCS') as HTMLElement;
                          if (divPCS && titleTable && navTable) {
                              const maxModalHeight = window.innerHeight * 0.8;
                              const maxIframeHeight = maxModalHeight - titleTable.offsetHeight - navTable.offsetHeight;
                              if (contentHeight > maxIframeHeight) {
                                
                                  iframe.style.height = `${maxIframeHeight}px`;
                                  iframe.style.overflowY = 'auto';
                              } else {
                                
                                  iframe.style.height = `${Math.max(contentHeight, 80)}px`;
                                  iframe.style.overflowY = 'hidden';
                              }
                          } else {
                              iframe.style.height = `${Math.max(contentHeight, 80)}px`;
                          }
                        }, 50);
                    }}
                />
              )}
            </div>

            {/* Footer / Navigation */}
            <table id="tblNavPCS" className="principle" cellSpacing="0" cellPadding="0" width="100%" style={{ height: '50px', fontSize: "15px" }}>
              <tbody>
                <tr style={{ backgroundColor: '#F2F2F2' }}>
                  {/* Previous Button */}
                  <td align="left" style={{ width: '33%', paddingLeft: '15px' }}>
                    {currentPrcIndex > 0 && (
                        <input 
                            type='button' 
                            value='PREVIOUS' 
                            onClick={() => {
                                const prevIndex = currentPrcIndex - 1;
                                setCurrentPrcIndex(prevIndex);
                                const folderParam = new URLSearchParams(window.location.search).get('folder') || '';
                                const countryParam = params.get('country') || 'Australia';
                                fetchPrincipleContent(principleList[prevIndex], folderParam, countryParam).then(data => setPrcContent(data.principleHtml || ""));
                            }}
                            style={{
                                fontWeight: 'bold', 
                                color: 'White', 
                                fontSize: '12pt', 
                                borderStyle: 'none', 
                                backgroundColor: 'maroon', 
                                cursor: 'pointer',
                                padding: '5px 10px'
                            }} 
                        />
                    )}
                  </td>
                  
                  <td style={{ width: '34%' }}>&nbsp;</td>
                  
                  {/* Next / Got It Button */}
                  <td align="right" style={{ width: '33%', paddingRight: '15px' }}>
                    {currentPrcIndex < principleList.length - 1 ? (
                         <img 
                            src={NextStepBtnSrc} 
                            alt="Next" 
                            onClick={() => {
                                const nextIndex = currentPrcIndex + 1;
                                setCurrentPrcIndex(nextIndex);
                                const folderParam = new URLSearchParams(window.location.search).get('folder') || '';
                                const countryParam = params.get('country') || 'Australia';
                                fetchPrincipleContent(principleList[nextIndex], folderParam, countryParam).then(data => setPrcContent(data.principleHtml || ""));
                            }}
                            onMouseOver={handleNextStepBtnMouseOver} 
                            onMouseOut={handleNextStepBtnMouseOut} 
                            style={{ cursor: 'pointer', width: '100px', height: '40px', marginRight: '30px', marginTop: '5px', marginBottom: '5px' }} 
                         />
                    ) : (
                         <img 
                            src={GotItBtnSrc} 
                            id="imgGotIt" 
                            alt="Got It" 
                            onClick={() => { setShowPrcModal(false); setIsPrcLoading(false); }} 
                            onMouseOver={handleGotItBtnMouseOver} 
                            onMouseOut={handleGotItBtnMouseOut} 
                            style={{ cursor: 'pointer', width: '100px', height: '40px', marginRight: '30px', marginTop: '5px', marginBottom: '5px' }} 
                         />
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}

        {showSolnModal && (
                <>
                    <div id="divReadOnlyScreen" style={{ display: 'block' }} onClick={() => {
                        setShowSolnModal(false);
                    }}></div>

                    <div id="divPCS" style={{ display: 'block', position: 'fixed', bottom: '0', width: '100%', zIndex: 10000 }}>
                        <table id="tblTitlePCS" className="solution" cellSpacing="0" cellPadding="0" width="100%">
                            <tbody>
                                <tr>
                                    <td id="tdPCSTitle1">Step by step solution</td>
                                    <td id="tdPCSClose" align="right" valign="top">
                    <button
                      type="button"
                      aria-label="Close solution"
                      onClick={() => setShowSolnModal(false)}
                      style={{ width: '30px', height: '30px', margin: '5px 15px 5px 0', padding: 0, border: 0, borderRadius: 0, backgroundColor: '#ff6500', color: '#fff', fontSize: '18px', lineHeight: '30px', textAlign: 'center', cursor: 'pointer' }}
                    >
                      ×
                    </button>
                  </td>
                                </tr>
                            </tbody>
                        </table>

                        <div id="divFramePCS">
                            {isSolnLoading ? <Loading /> : (
                                <iframe
                                    id="iFrameSolution"
                                    ref={solnIframeRef}
                                    title="Solution"
                                    srcDoc={solnContent}
                                    style={{ width: '100%', border: 'none', minHeight: '50px' }}
                                    onLoad={() => {
                                        const iframe = solnIframeRef.current;
                                        if (!iframe || !iframe.contentDocument || !iframe.contentWindow) {
                                            console.error("Iframe document or window not ready in onLoad.");
                                            return;
                                        }
                                        try {
                                            const iframeDoc = iframe.contentDocument;
                                            const stepsInput = iframeDoc.getElementById('NoOfSteps') as HTMLInputElement;
                                            const totalSteps = parseInt(stepsInput?.value || '0', 10);
                                            setNoOfSteps(totalSteps);

                                            const isAutoExpanded = iframeDoc.getElementById('IsAutoExpanded');
                                        
                                            const stepRows = iframeDoc.querySelectorAll<HTMLElement>('[id^="trSolStepLHS"]');
                                            stepRows.forEach((step) => {
                                              step.querySelectorAll<HTMLElement>(':scope > td').forEach((cell) => {
                                                if (!cell.style.color && !cell.hasAttribute('color')) cell.style.color = 'black';
                                              });
                                            });
                                            let firstStepHeight = 150;
                                        
                                            stepRows.forEach((step, index) => {
                        const rhsCell = step.querySelector<HTMLElement>(':scope > td:last-of-type');
                        rhsCell?.style.setProperty('text-align', 'left', 'important');
                        rhsCell?.style.setProperty('padding-left', '12px', 'important');
                                                if (index <= currentSolnNo || isAutoExpanded) {
                                                    step.style.display = 'table-row';
                                                } else {
                                                    step.style.display = 'none';
                                                }
                                            });
                                        
                                            if (totalSteps === 0) {
                                                iframe.style.height = '50px';
                                            }

                                            setTimeout(() => {
                                                if (!iframe.contentDocument || !iframe.contentDocument.body) return;

                                                const contentHeight = iframe.contentDocument.body.scrollHeight + 5;

                                                const divPCS = document.getElementById('divPCS') as HTMLElement;
                                                const titleTable = document.getElementById('tblTitlePCS') as HTMLElement;
                                                const navTable = document.getElementById('tblNavPCS') as HTMLElement;

                                                if (divPCS && titleTable && navTable) {
                                                    const maxModalHeight = window.innerHeight * 0.45; 
                                                    const currentModalHeight = divPCS.offsetHeight;
                                                    const maxIframeHeight = maxModalHeight - titleTable.offsetHeight - navTable.offsetHeight;

                                                    if (contentHeight > maxIframeHeight) {
                                                        iframe.style.height = `${maxIframeHeight}px`;
                                                        iframe.style.overflowY = 'auto';
                                                    } else {
                                                        iframe.style.height = `${Math.max(contentHeight, 50)}px`;
                                                        iframe.style.overflowY = 'hidden';
                                                    }
                                                }

                                                // If Auto Expanded, update state to show "Got It" immediately
                                                if (isAutoExpanded) {
                                                    setCurrentSolnNo(totalSteps - 1);
                                                    if (solutionSeen !== 'Y') {
                                                        setSolutionSeen('Y');
                                                    }
                                                    iframe.contentWindow?.scrollTo(0, iframe.contentDocument.body.scrollHeight);
                                                } else {
                                                    // Manual mode initial step height
                                                    if (stepRows.length > 0) {
                                                        firstStepHeight = stepRows[0].scrollHeight + 5;
                                                        iframe.style.height = `${Math.max(firstStepHeight, 150)}px`;
                                                        iframe.style.overflowY = 'hidden';
                                                    }
                                                }
                                            }, 50);
                                        
                                            iframeDoc.querySelectorAll<HTMLImageElement>('img[id^="KsdImg"]').forEach(img => {
                        const index = img.id.substring(6);
                        const ksdDiv = iframeDoc.getElementById(`KsdDiv${index}`);
                        const content = ksdDiv?.getAttribute('data-ksd-content');
                        const wasPreviouslyRevealed = revealedSolnSteps.includes(Number(index));
                        const showCell = img.closest('td');
                        showCell?.style.setProperty('text-align', 'left', 'important');
                        showCell?.style.setProperty('padding-left', '12px', 'important');
                        const answerCell = ksdDiv?.closest('td') as HTMLTableCellElement | null;

                        const revealAnswer = () => {
                          if (!content || !showCell || !answerCell) return;
                          showCell.remove();
                          answerCell.colSpan = 2;
                          answerCell.innerHTML = content;
                          answerCell.style.setProperty('width', '50%', 'important');
                          answerCell.style.setProperty('max-width', 'none', 'important');
                          answerCell.style.setProperty('vertical-align', 'middle', 'important');
                          answerCell.style.setProperty('text-align', 'left', 'important');
                          answerCell.style.setProperty('padding-left', '12px', 'important');
                          answerCell.querySelectorAll<HTMLElement>('td').forEach(cell => {
                            cell.style.setProperty('text-align', 'left', 'important');
                            cell.style.setProperty('padding-left', '12px', 'important');
                          });
                        };

                        if (content && answerCell && wasPreviouslyRevealed) {
                          revealAnswer();
                          return;
                        }

                        img.onclick = () => {
                          if (img.getAttribute('alt') === '1') {
                            revealAnswer();
                            setRevealedSolnSteps(previous => previous.includes(Number(index)) ? previous : [...previous, Number(index)]);
                          }
                        };
                      });
                                        
                                        } catch (e) {
                                            console.error("Error inside solution iframe onLoad:", e);
                                        }
                                    }}
                                />
                            )}
                        </div>

                        <table id="tblNavPCS" className="solution" cellSpacing="0" cellPadding="0" width="100%" style={{ backgroundColor: '#F2F2F2' }}>
                            <tbody>
                                <tr>
                                    <td align="left" style={{ width: '33%', paddingLeft: '15px' }}>
                                    </td>

                                    <td style={{ width: '34%' }}>
                                        &nbsp;
                                    </td>

                                    <td align="right" style={{ width: '33%', paddingRight: '15px' }}>
                                        
                                        {!isSolnLoading && noOfSteps > 0 && currentSolnNo < noOfSteps - 1 && (
                                            <img
                                                id="btnPCSNext"
                                                src={NextStepBtnSrc}
                                                alt="Next Step"
                                                onClick={() => {
                                                    const nextStep = currentSolnNo + 1;
                                                    const iframe = solnIframeRef.current;

                                                    if (iframe?.contentDocument?.body) {
                                                        const iframeDoc = iframe.contentDocument;

                                                        const stepRows = iframeDoc.querySelectorAll<HTMLElement>('[id^="trSolStepLHS"]');
                                                        if (nextStep >= stepRows.length) return;

                                                        const nextStepElement = stepRows[nextStep];
                                                        nextStepElement.style.display = 'table-row';
                                                    
                                                        setTimeout(() => {
                                                            if (!iframe.contentDocument || !iframe.contentDocument.body) return;

                                                            const totalRequiredHeight = iframe.contentDocument.body.scrollHeight + 5;

                                                            const divPCS = document.getElementById('divPCS') as HTMLElement;
                                                            const titleTable = document.getElementById('tblTitlePCS') as HTMLElement;
                                                            const navTable = document.getElementById('tblNavPCS') as HTMLElement;

                                                            if (!divPCS || !titleTable || !navTable) return;

                                                            const maxModalHeight = window.innerHeight * 0.45;
                                                            const chromeHeight = titleTable.offsetHeight + navTable.offsetHeight;
                                                            const maxAllowedIframeHeight = maxModalHeight - chromeHeight;

                                                            if (totalRequiredHeight > maxAllowedIframeHeight) {
                                                                iframe.style.height = `${maxAllowedIframeHeight}px`;
                                                                iframe.style.overflowY = 'auto';
                                                            } else {
                                                                iframe.style.height = `${totalRequiredHeight}px`;
                                                                iframe.style.overflowY = 'hidden';
                                                            }
                                                            iframe.contentWindow?.scrollTo(0, iframe.contentDocument.body.scrollHeight);
                                                        
                                                        }, 50);
                                                    
                                                        setCurrentSolnNo(nextStep);
                                                        if (solutionSeen !== 'Y') {
                                                            setSolutionSeen(nextStep + 1);
                                                        }
                                                    }
                                                }}
                                                onMouseOver={handleNextStepBtnMouseOver}
                                                onMouseOut={handleNextStepBtnMouseOut}
                                                style={{ cursor: 'pointer' }}
                                            />
                                        )}
                                        
                                        {!isSolnLoading && (noOfSteps === 0 || currentSolnNo === noOfSteps - 1) && (
                                            <img
                                                id="btnPCSClose"
                                                src={GotItBtnSrc}
                                                alt="Got It"
                                                onClick={() => {
                                                    setShowSolnModal(false);
                                                    setSolutionSeen('Y');
                                                }}
                                                onMouseOver={handleGotItBtnMouseOver}
                                                onMouseOut={handleGotItBtnMouseOut}
                                                style={{ cursor: 'pointer', display: isSolnLoading ? 'none' : 'inline-block' }}
                                            />
                                        )}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </>
            )}

        </>
    );
};

export default forwardRef(IQns);;
